import React, { useState } from 'react';
import { Shield, Eye, Edit3, Check, X, Lock, Unlock, AlertCircle } from 'lucide-react';
import {
  useFieldPermissions,
  useUpdateFieldPermission,
  useBulkUpdateFieldPermissions,
  type FieldPermission
} from '../../../services/queries/fieldPermissionQueries';
import { useRoles } from '../../../services/queries/roleQueries';
import { useToast } from '../../../contexts/ToastContext';

interface FieldSecurityManagerProps {
  fieldId: string;
  fieldLabel: string;
  objectName: string;
  mode?: 'full' | 'compact'; // full = standalone page, compact = in field editor
}

export const FieldSecurityManager: React.FC<FieldSecurityManagerProps> = ({
  fieldId,
  fieldLabel,
  objectName,
  mode = 'compact'
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, { canView: boolean; canEdit: boolean }>>(new Map());

  const { data: permissions, isLoading: permissionsLoading } = useFieldPermissions(fieldId);
  const { data: roles, isLoading: rolesLoading } = useRoles();
  const updatePermissionMutation = useUpdateFieldPermission();
  const bulkUpdateMutation = useBulkUpdateFieldPermissions();
  const { success, error } = useToast();

  const isLoading = permissionsLoading || rolesLoading;

  // Get current or pending permission for a role
  const getPermission = (roleId: string): { canView: boolean; canEdit: boolean } => {
    if (pendingChanges.has(roleId)) {
      return pendingChanges.get(roleId)!;
    }
    const perm = permissions?.find(p => p.roleId === roleId);
    return {
      canView: perm?.canView ?? true,
      canEdit: perm?.canEdit ?? true
    };
  };

  // Update permission (pending)
  const handlePermissionChange = (roleId: string, field: 'canView' | 'canEdit', value: boolean) => {
    const current = getPermission(roleId);
    let newCanView = current.canView;
    let newCanEdit = current.canEdit;

    if (field === 'canView') {
      newCanView = value;
      // If disabling view, must also disable edit
      if (!value) {
        newCanEdit = false;
      }
    } else {
      newCanEdit = value;
      // If enabling edit, must also enable view
      if (value) {
        newCanView = true;
      }
    }

    setPendingChanges(new Map(pendingChanges.set(roleId, { canView: newCanView, canEdit: newCanEdit })));
    setHasChanges(true);
  };

  // Quick Actions
  const handleGrantReadToAll = () => {
    const newChanges = new Map(pendingChanges);
    roles?.forEach(role => {
      newChanges.set(role.id, { canView: true, canEdit: getPermission(role.id).canEdit });
    });
    setPendingChanges(newChanges);
    setHasChanges(true);
  };

  const handleAdminsOnly = () => {
    const newChanges = new Map(pendingChanges);
    roles?.forEach(role => {
      const isAdmin = role.name === 'System Administrator' || role.name.includes('Admin');
      newChanges.set(role.id, {
        canView: true,
        canEdit: isAdmin
      });
    });
    setPendingChanges(newChanges);
    setHasChanges(true);
  };

  const handleRevokeAll = () => {
    const newChanges = new Map(pendingChanges);
    roles?.forEach(role => {
      // Keep System Administrator permissions
      const isSystemAdmin = role.name === 'System Administrator';
      newChanges.set(role.id, {
        canView: isSystemAdmin,
        canEdit: isSystemAdmin
      });
    });
    setPendingChanges(newChanges);
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    if (pendingChanges.size === 0) return;

    try {
      const updates = Array.from(pendingChanges.entries()).map(([roleId, perms]) => ({
        roleId,
        ...perms
      }));

      await bulkUpdateMutation.mutateAsync({
        fieldId,
        updates
      });

      success('Field permissions updated successfully!');
      setPendingChanges(new Map());
      setHasChanges(false);
    } catch (err: any) {
      error('Failed to update permissions', err.message || 'Please try again');
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setPendingChanges(new Map());
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Loading permissions...</p>
      </div>
    );
  }

  const rolesCanView = roles?.filter(r => getPermission(r.id).canView).length || 0;
  const rolesCanEdit = roles?.filter(r => getPermission(r.id).canEdit).length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      {mode === 'full' && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Field-Level Security
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {fieldLabel} • Control who can view and edit this field
              </p>
            </div>
          </div>
        </div>
      )}

      {mode === 'compact' && (
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            Field-Level Security
          </h4>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800 dark:text-blue-300">
            Control which roles can view and edit this field. By default, admins can edit and everyone can view.
            {hasChanges && (
              <span className="block mt-1 font-semibold">
                ⚠️ You have unsaved changes. Click "Save Permissions" to apply.
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleGrantReadToAll}
          className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-1"
        >
          <Eye className="w-3 h-3" />
          Grant Read to All
        </button>
        <button
          onClick={handleAdminsOnly}
          className="px-3 py-1.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors flex items-center gap-1"
        >
          <Lock className="w-3 h-3" />
          Admins Only
        </button>
        <button
          onClick={handleRevokeAll}
          className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-1"
        >
          <X className="w-3 h-3" />
          Revoke All
        </button>
      </div>

      {/* Permission Matrix */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Eye className="w-3 h-3" />
                    View
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center gap-1">
                    <Edit3 className="w-3 h-3" />
                    Edit
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {roles?.map((role) => {
                const perm = getPermission(role.id);
                const hasChange = pendingChanges.has(role.id);

                return (
                  <tr
                    key={role.id}
                    className={hasChange ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {role.name}
                        </span>
                        {hasChange && (
                          <span className="text-xs text-yellow-600 dark:text-yellow-400">
                            (unsaved)
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {role.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={perm.canView}
                        onChange={(e) => handlePermissionChange(role.id, 'canView', e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={perm.canEdit}
                        onChange={(e) => handlePermissionChange(role.id, 'canEdit', e.target.checked)}
                        disabled={!perm.canView}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={!perm.canView ? 'Enable view permission first' : 'Allow editing'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security Summary */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
        <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
          Security Summary
        </h4>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Roles with View Access:</span>
            <span className="ml-2 font-semibold text-gray-900 dark:text-white">
              {rolesCanView} of {roles?.length || 0}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Roles with Edit Access:</span>
            <span className="ml-2 font-semibold text-gray-900 dark:text-white">
              {rolesCanEdit} of {roles?.length || 0}
            </span>
          </div>
        </div>
        {permissions && permissions.length > 0 && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Last modified {new Date(permissions[0].lastModified).toLocaleDateString()} by {permissions[0].lastModifiedBy}
          </div>
        )}
      </div>

      {/* Save/Cancel Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={bulkUpdateMutation.isPending}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {bulkUpdateMutation.isPending ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FieldSecurityManager;
